import {combineLatest, map, Observable} from "rxjs";
import {Capacitor} from "@capacitor/core";
import {ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot} from "@angular/router";
import {RouteData} from "./app.routes";
import {AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {Device} from "@capacitor/device";


export function enforceType<T>(input: T): T {
  return input;
}

export const atLeastOneRequiredValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  if (group instanceof FormGroup) {
    const hasNoError = Object.values(group.controls).map(
      control => Validators.required(control)
    ).some(result => result === null);
    return hasNoError ? null : {atLeastOneRequired: true};
  }

  return null;
};

export function joinById<OriginalKey extends string,
  JoinKey extends string,
  Original extends Record<OriginalKey, string>,
  JoinedWith extends Record<JoinKey, string>,
  To
>(originals: Observable<Original[]>,
  joinWith: Observable<JoinedWith[]>,
  originalKey: OriginalKey,
  joinKey: JoinKey,
  mapFunction: (original: Original, joinedWith: JoinedWith) => To,
  mapNotFound: (original: Original) => To): Observable<To[]> {
  return combineLatest([originals, joinWith]).pipe(map(([originals, joinWith]) => originals.map(
    original => {
      const joinedWith = joinWith.find(joinedWith => {
        const joinedWithElementId: string = joinedWith[joinKey];
        const originalElementId: string = original[originalKey];
        return joinedWithElementId === originalElementId;
      });
      return joinedWith ? mapFunction(original, joinedWith) : mapNotFound(original);
    })));
}

export const platform: 'web' | 'android' | 'ios' = Capacitor.getPlatform() as any;
export const isNative = Capacitor.isNativePlatform();

export const isMobile = async () => {
  const info = await Device.getInfo();
  return info.operatingSystem === 'android';
}

export const getAllRouteData = (activatedRoute: ActivatedRoute): RouteData => {
  const childrenRouteData = getChildrenRouteData(activatedRoute.root);
  return {...childrenRouteData};
}

const getChildrenRouteData = (activatedRoute: ActivatedRoute): Record<string, any> => {
  const data = activatedRoute.firstChild ? getChildrenRouteData(activatedRoute.firstChild) : {};
  return {...activatedRoute.snapshot.data, ...data};
}

export const getAllRouterStateData = (activatedRoute: RouterStateSnapshot): RouteData => {
  const childrenRouteData = getChildrenRouterStateData(activatedRoute.root);
  return {...childrenRouteData};
}

export const getChildrenRouterStateData = (activatedRoute: ActivatedRouteSnapshot): Record<string, any> => {
  const data = activatedRoute.firstChild ? getChildrenRouterStateData(activatedRoute.firstChild) : {};
  return {...activatedRoute.data, ...data};
}

export const getDeepestChildRoute = (activatedRoute: ActivatedRoute): ActivatedRoute => {
  return activatedRoute.firstChild ? getDeepestChildRoute(activatedRoute.firstChild) : activatedRoute;
}

export const getSnapshotDeepestChildRoute = (activatedRoute: ActivatedRouteSnapshot): ActivatedRouteSnapshot => {
  return activatedRoute.firstChild ? getSnapshotDeepestChildRoute(activatedRoute.firstChild) : activatedRoute;
}

export const isAllZero = (data: Record<string, number | undefined> | any) => {
  return Object.values(data).every(value => !value);
}

export const deepChildRoute = (activatedRoute: ActivatedRoute): ActivatedRoute => {
  return activatedRoute.firstChild ? deepChildRoute(activatedRoute.firstChild) : activatedRoute;
}

export const validatedNotNull = <T>(value: T | null | undefined): T => {
  if (value === null || value === undefined) {
    throw new Error('Value is null or undefined');
  }
  return value;
}

export const waitAsync = (millis: number) => new Promise(
  resolve => setTimeout(resolve, millis)
)

export function generateRandomNumber(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function generateRandom8DigitNumber(): number {
  return generateRandomNumber(10000000, 99999999);
}

export function getCookie(name: string): string | undefined {
  const nameEQ = name + "=";
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }
  return undefined;
}

export function retryImport<T>(
  loader: () => Promise<T>,
  retries = 3,
  delay   = 400   // ms; doubles every attempt
): () => Promise<T> {

  return () =>
    loader().catch(err => {
      if (retries === 0) {
        // last attempt also failed → bubble the error so
        // Angular’s normal NavigationError handling still works

        throw new Error(
          'This page failed to load, please reload and try again.',
          {cause: err}
        );
      }

      console.warn(
        `Lazy chunk failed, ${retries} retries left…`,
        err
      );

      // wait a bit, then try again (exponential back-off)
      return new Promise(res => setTimeout(res, delay))
        .then(() => retryImport(loader, retries - 1, delay * 2)());
    });
}
